import type { HandoverOverview } from '@testflow/shared';
import type { TFunction } from 'i18next';
import type { ReactNode } from 'react';
import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { E2EBadge } from '../../components/atoms.js';
import { QueryErrorNotice } from '../../components/query-error-notice.js';
import { SearchEmptyNotice } from '../../components/search-empty-notice.js';
import { commonTargets, isHandoverCard, selectedEntryCount } from '../../lib/handover-selection.js';
import { pickLang } from '../../lib/pick-lang.js';
import {
  GROUP_ORDER,
  type PlaylistEntryRef,
  type PlaylistGroupKey,
  groupEntries,
  groupOf,
  isLocked,
  playlistCounts,
  playlistEntryKey,
} from '../../lib/playlist-groups.js';
import { matchesQuery } from '../../lib/row-filter.js';
import type { PlaylistEntry } from '../../lib/runs-api.js';
import { CardFrame } from './card-frame.js';
import { FixCheckCards, type FixCheckListProps, fixCheckMatches } from './fix-check-card.js';
import { HandoverBar } from './handover-bar.js';

// Design-Spec §3.3 (Screen 2b dark / 1c light): linke Playlist-Spalte (360px). Karten mit ID
// (Mono), E2E-Badge, Titel, Meta (Tcode). Priorisierung (EXE-2) kommt bereits sortiert vom
// Server (`runsApi.playlist`) — hier wird nur der Zustand jeder Karte dargestellt: aktiv/
// ausgewählt (Orange-Border), `retest` (oranges Retest-Label + Defect-Chip, s. u.), Standard.
// Task 4 (#113): Der frühere Zustand `up_next` (gelber Tint + „du bist gleich dran") ist ersatzlos
// entfallen — er behauptete eine Reihenfolge-Aussage, die er nie getroffen hat.
// Task 10 (#113, A4): Sein Nachfolger `waiting` (Vorgänger noch nicht bestanden) ist jetzt
// dargestellt — GRAU und nicht anklickbar, mit „wartet auf {Tester} — Schritt {N}: {Titel}".
// Grau, NICHT gelb: Gelb gehört dem Lauf-Ergebnis „Blockiert" (Gruppe `blocked`). `waiting` ist
// etwas anderes — „dein Vorgänger ist noch nicht fertig" —, und zwei Sachverhalte dürfen weder
// einen Namen noch eine Farbe teilen (anforderung.md, Risiko-Tabelle: verbindlich).

// #190: „Defect ETO-DEF-004 abgelehnt" bzw. mit Grund. Leerer Grund zählt wie keiner.
function rejectedText(t: TFunction, rejected: { human_id: string; reason: string | null }): string {
  const reason = rejected.reason?.trim();
  return reason
    ? t('exec.rejectedDefectReason', { id: rejected.human_id, reason })
    : t('exec.rejectedDefect', { id: rejected.human_id });
}

// #128: exportiert — die Ansicht „nach E2E-Prozess" (chain-groups.tsx) zeigt eigene Kettenglieder
// mit GENAU DIESER Karte, damit Auswahl, Sperre und „wartet auf" dort nicht nachgebaut werden.
export function PlaylistCard({
  entry,
  selected,
  onSelect,
  canViewDefect,
  onOpenDefect,
  showChain = true,
  handoverCheck,
}: {
  entry: PlaylistEntry;
  selected: boolean;
  onSelect: () => void;
  // #92: steuert den Defect-Chip-Link der Retest-Sektion (can()-Muster aus app-shell.tsx:256-260).
  // Playlist/PlaylistCard kennen selbst keine Rollen — der Aufrufer (my-tests-page.tsx) berechnet
  // das einmal und reicht das Ergebnis durch.
  canViewDefect: boolean;
  // #192: Tester (ohne defect.edit) öffnen den Defect des Retest-Chips in der Nur-Lesen-Seitenleiste
  // der Seite. Fehlt die Prop, bleibt der Chip reiner Text (bisheriges Verhalten).
  onOpenDefect?: ((defectId: string) => void) | undefined;
  // #128: In der Ketten-Ansicht steht der Kettenname bereits in der Gruppenüberschrift — dort wäre
  // er auf jeder Karte nur verbrauchte Zeile in der 360px-Spalte.
  showChain?: boolean;
  // #195: Auswahlkästchen „Weitergeben" links in der Karte. Fehlt die Prop (andere Ansichten, Rolle
  // ohne run.handover, Vertretung, erledigt), gibt es kein Kästchen. `disabledReason` ist der
  // fertige Tooltip-Text, wenn der Testfall keine Ziele hat. Das Kästchen ist ein eigenes
  // Bedienelement: sein Klick wählt NICHT die Karte zum Ausführen aus.
  handoverCheck?:
    | { checked: boolean; disabledReason: string | null; onToggle: () => void }
    | undefined;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  // #194 (N4): Die ID steht in der Kopfzeile außerhalb des Inhalts-Buttons; der Name des Buttons
  // setzt sich per aria-labelledby aus ID und eigenem Inhalt zusammen (sonst fehlt sie Screenreadern).
  const uid = useId();
  const idLabelId = `${uid}-id`;
  const buttonId = `${uid}-btn`;
  // Task 10 (A4): „Ein Kettenglied ist nicht ausführbar, solange sein direkter Vorgänger nicht
  // successful ist." Die Sperre selbst sitzt serverseitig (Task 5, 409 predecessor_not_done); hier
  // wird sie sichtbar, damit der Tester gar nicht erst hineinläuft. Zwei Wege, beide nötig: der
  // Karten-Klick (Maus) bekommt keinen Handler, der Inhalts-Button (Tastatur) wird `disabled` —
  // die Maus-Sperre allein ließe den Tastaturweg offen.
  //
  // Review-Befund Important 1: gefragt wird das Vorgänger-Urteil, NICHT `state === 'waiting'`. Die
  // Zustands-Rangfolge (retest → error → waiting) verdeckt die Sperre an jedem Glied, das einen
  // eigenen Fehler- oder Retest-Lauf hat — genau diese Karten waren weiß, anklickbar und als
  // „offen" gezählt, obwohl der Server sie abweist. `state` bleibt für Farbe und Label zuständig
  // (s. das Fehler-/Retest-Label weiter unten); die Trennung ist der ganze Punkt.
  //
  // Whole-Branch-Review, Important 2: `isLocked` und nicht `isBlocked` — die eine Anzeige-
  // Rücksicht. Ein ERLEDIGTES Glied trägt seit der Entkopplung ebenfalls ein `blocked_by`, wenn
  // sein Vorgänger nachträglich wieder rot geworden ist; ausgegraut in der Gruppe „Erledigt" wäre
  // das eine Aussage über Arbeit, die längst getan ist. Ob ein WIEDERHOLUNGSLAUF durchgeht, ist
  // eine andere Frage — die beantwortet das ExecutionPanel über `blocked_by` selbst.
  const locked = isLocked(entry);
  // Review-Befund Minor 2: `e2e_name_en` ist `notNull().default('')` (schema.ts:296) — eine
  // unübersetzte Kette liefert '' und nicht null. Ohne den Rückfall stünde in der englischen
  // Oberfläche das namenlose „E2E", obwohl ein deutscher Name vorliegt.
  const chainName = pickLang(lang, entry.e2e_name_de, entry.e2e_name_en);
  // #211: derselbe Sprach-Rückfall wie beim Kettennamen (`processes.name_en` ist ebenfalls
  // `notNull().default('')`).
  const processName = pickLang(lang, entry.process_name_de, entry.process_name_en);
  // #190: Hinweistext für einen abgelehnten Defect — einmal berechnet, Text und title teilen ihn.
  const rejectedHint = entry.rejected_defect ? rejectedText(t, entry.rejected_defect) : null;
  // #194 (N4): Zustands-Label rechts in der Kopfzeile. Retest und gemeldeter Fehler (#31, Finding 25:
  // noch bei niemandem in Arbeit — vorher stand hier „Retest", ein Nachtest-Auftrag ohne Reparatur);
  // bei „offen" bleibt die Kopfzeile rechts leer. Die Kopfzeile ist immer neutral; nur das Wort
  // „Retest" ist orange (#194, 15.10.), das Label „Fehler" steht ohne Orange.
  // #194 Final-Review (Spec B5a „Zustand rechts"): ein blockierter Lauf (Gruppe „Blockiert") ist
  // ebenfalls ein Zustand — „Blockiert" wie der Knopf der Ausführung, neutraler Ton. Kein
  // Aktions-Label (#93: Blockiert heißt „warte", nicht „teste nochmal"), nur die Benennung.
  const stateLabel =
    entry.state === 'retest'
      ? t('exec.retest')
      : entry.state === 'error'
        ? t('exec.reportedError')
        : entry.last_result === 'blocked'
          ? t('exec.blocked')
          : null;

  return (
    // #92: Container statt <button> — der Defect-Chip unten braucht bei defect.edit einen echten
    // Link, und <a> im <button> ist ungueltiges HTML (derselbe Grund wie der #43-Umbau von
    // DefectCard). Der Tastaturweg bleibt der Inhalts-Button darunter: ein per Tastatur
    // ausgeloester Klick darauf bubbelt zu diesem onSelect hoch. Der Chip stoppt die Weitergabe
    // selbst, sonst waehlt ein Klick auf ihn zusaetzlich die Karte aus, bevor er navigiert.
    // #194 (N4): Kartenrahmen mit Kopfzeile (card-frame.tsx) — links Testfall-ID, rechts das
    // Zustands-Label. #194 (15.10.): Testkarten sind immer neutral gerahmt (zu viel Rot); bei Retest
    // ist nur das Zustandswort orange (`rightAccent`). Die Auswahl ist der Ring des Rahmens.
    <CardFrame
      onClick={locked ? undefined : onSelect}
      // Review-Befund Minor 4: `opacity-60` ist eine reine Farbaussage — Hilfsmittel lesen den
      // Zustand aus `aria-disabled`. Der Inhalts-Button trägt `disabled`, der Container konnte den
      // Zustand bisher nur einfärben.
      aria-disabled={locked || undefined}
      className={locked ? 'opacity-60' : ''}
      tone="neutral"
      selected={selected}
      hoverable={!locked}
      header={{
        leftId: idLabelId,
        leading: handoverCheck && (
          <span title={handoverCheck.disabledReason ?? undefined} className="inline-flex">
            <input
              type="checkbox"
              aria-label={t('handover.select', { id: entry.human_id })}
              title={handoverCheck.disabledReason ?? undefined}
              checked={handoverCheck.checked}
              disabled={handoverCheck.disabledReason !== null}
              onChange={handoverCheck.onToggle}
              onClick={(e) => e.stopPropagation()}
              className="h-4 w-4 accent-brand-accent disabled:opacity-50"
            />
          </span>
        ),
        // Task 10 (A4): Ohne Kettennamen (Kette ohne Namenszeile) bleibt es beim namenlosen
        // „E2E"-Abzeichen. Mit Namen steht er seit #128 in einer eigenen Zeile darunter.
        left: (
          <>
            {entry.human_id}
            {showChain && entry.e2e_id && !chainName && (
              <span className="ml-2 align-middle">
                <E2EBadge />
              </span>
            )}
          </>
        ),
        right: stateLabel,
        rightAccent: entry.state === 'retest',
      }}
    >
      <button
        id={buttonId}
        type="button"
        disabled={locked}
        aria-labelledby={`${idLabelId} ${buttonId}`}
        // Der Rahmen schneidet mit overflow-hidden ab: der Fokusrahmen sitzt deshalb innen
        // (negativer Offset), sonst wäre er am Kartenrand unsichtbar.
        className="flex w-full flex-col items-start gap-1 px-3 py-2 text-left focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-text-primary"
      >
        {/* Whole-Branch-Review, Minor 4: sprachabhängig aufgelöst wie in der Kettenleiste
            (chain-bar.tsx). Hier stand hart `entry.title_de` — in der englischen Oberfläche
            mischte eine Karte damit zwei Sprachen. Rückfall auf die jeweils andere Sprache, weil
            beide Felder leer sein können.
            #211 Nachtrag: Mit Prozess, Kette und Tcode war die Karte überladen. Der Titel steht
            deshalb OBEN und fett, Prozess und Kette darunter als gedämpfter Kontext; der Tcode ist
            von der Karte genommen (er steht im Ausführungsfeld). */}
        <span className="text-sm font-semibold text-text-body">
          {pickLang(lang, entry.title_de, entry.title_en)}
        </span>
        {/* #211: der Prozess des Testfalls. Unabhängig von `showChain` — in der Ansicht nach
            E2E-Prozess steht die Kette im Gruppenkopf, der Prozess aber nirgends sonst.
            #128 (offene Frage 6): der Kettenname als EIGENE Zeile, bis zu zwei Zeilen, ohne
            `truncate` und ohne `title` — bei Namen wie „Auftrag bis Zahlung mit Anzahlung" ist
            das Ende genau der Teil, der zwei Ketten unterscheidet. */}
        {(processName || (showChain && entry.e2e_id && chainName)) && (
          <span className="flex flex-col gap-0.5">
            {processName && (
              <span data-process-name className="text-[11px] text-text-muted line-clamp-2">
                {t('exec.processLabel')} · {processName}
              </span>
            )}
            {showChain && entry.e2e_id && chainName && (
              <span data-chain-name className="text-[11px] text-text-muted line-clamp-2">
                E2E · {chainName}
              </span>
            )}
          </span>
        )}
        {/* #50 (PLAN-5): Herkunft eines vertretenen Items. Die Zuständigkeit bleibt beim
            Abwesenden — die Karte macht sichtbar, dass dieses Item nicht dem Vertreter selbst
            gehört. */}
        {entry.substituted_for && (
          <span className="rounded-chip bg-[var(--tint-blue)] px-2 py-0.5 text-[11px] text-text-body">
            {t('exec.substitutedFor', { name: entry.substituted_for.name })}
          </span>
        )}
        {/* #190: ein abgelehnter Defect holt den Testfall nach „Offen" zurück. Ohne diese Zeile
            wäre unerklärlich, warum eine Karte, die als Fehler gemeldet war, wieder wie neu
            aussieht. Der Grund ist der jüngste Kommentar am Defect; lang → zwei Zeilen, der volle
            Text steht im title. */}
        {rejectedHint && (
          <span
            data-rejected-defect
            title={rejectedHint}
            className="line-clamp-2 break-words text-[11px] text-status-warn"
          >
            {rejectedHint}
          </span>
        )}
        {/* Task 10 (A4): „wartet auf Schulz — Schritt 2: Lieferung". DERSELBE Schlüssel wie in der
            Kettenleiste (chain-bar.tsx) — ein zweiter Wortlaut für denselben Sachverhalt wäre eine
            zweite Wahrheit über dieselbe Aussage. Gedämpft (text-text-muted), nicht rot: hier ist
            nichts kaputt, hier ist nur jemand anderes noch nicht fertig. */}
        {/* Whole-Branch-Review, Important 2: an `locked` gehängt, nicht an `blocked_by`. Auf einer
            ERLEDIGTEN Karte wäre „wartet auf Schulz — Schritt 2" schlicht falsch: gewartet hat sie
            einmal, gelaufen ist sie längst. */}
        {/* #211 Nachtrag: als gedämpfter Chip statt loser Zeile — zwischen Titel, Prozess und
            Kette ging die Warteaussage sonst unter. */}
        {locked && entry.blocked_by && (
          <span className="rounded-chip bg-bg-subtle px-2 py-0.5 text-[11px] text-text-muted">
            {t('exec.chain.waitingFor', {
              name: entry.blocked_by.tester_name ?? t('exec.chain.unknownTester'),
              sequence: entry.blocked_by.sequence,
              title: pickLang(lang, entry.blocked_by.title_de, entry.blocked_by.title_en),
            })}
          </span>
        )}
      </button>
      {/* #92: Defect-Kontext des Nachtests (ID + Titel). Link auf /defects/:id NUR mit
          defect.edit — Tester ohne das Recht sehen denselben Text als reinen Chip, keine Route,
          die ihre Shell ohnehin nicht kennt (gleiche Regel wie deeplinkForNotification).
          #192: Tester öffnen stattdessen die Nur-Lesen-Seitenleiste (`onOpenDefect`) — sie sollen
          wissen, was behoben wurde, bevor sie nachtesten. */}
      {entry.state === 'retest' && entry.retest_defect && (
        // biome-ignore lint/a11y/useKeyWithClickEvents: kein eigenes Interaktionsziel, faengt nur die Klick-Weitergabe an die Karte ab (s. Kommentar am Container oben).
        <span
          onClick={(e) => e.stopPropagation()}
          className="mx-3 mb-2 self-start rounded-chip border border-[var(--border)] bg-bg-subtle px-2 py-0.5 text-[11px] text-text-body"
        >
          {canViewDefect ? (
            <Link
              to={`/defects/${entry.retest_defect.defect_id}`}
              className="text-brand-accent hover:underline"
            >
              {t('exec.retestDefect', { id: entry.retest_defect.human_id })}
            </Link>
          ) : onOpenDefect ? (
            <button
              type="button"
              onClick={() => onOpenDefect(entry.retest_defect?.defect_id ?? '')}
              aria-label={t('myDefects.openDefect', { id: entry.retest_defect.human_id })}
              className="text-brand-accent hover:underline"
            >
              {t('exec.retestDefect', { id: entry.retest_defect.human_id })}
            </button>
          ) : (
            <span>{t('exec.retestDefect', { id: entry.retest_defect.human_id })}</span>
          )}
          {' — '}
          {entry.retest_defect.title}
        </span>
      )}
    </CardFrame>
  );
}

// #93: Gruppen-Überschriftenfarbe folgt derselben Dringlichkeits-Logik wie die Karten-Labels
// (retest/error = status-fail, blocked = status-warn) — open/done bleiben neutral (kein
// Handlungsdruck).
const GROUP_HEADING_CLASS: Record<PlaylistGroupKey, string> = {
  retest: 'text-status-fail',
  error: 'text-status-fail',
  blocked: 'text-status-warn',
  open: 'text-text-muted',
  done: 'text-text-muted',
};

// Liest den Klapp-Zustand fail-soft aus sessionStorage: ein korrupter/fremder Wert unter dem
// Schluessel darf die Seite nicht bei jedem Reload crashen (auch valides JSON wie `5` wirft:
// `new Set(5)` ist ein TypeError). Unbekannte Elemente werden verworfen, Fallback ist der
// Default „nur 'done' zu".
function readCollapsed(stored: string | null): Set<PlaylistGroupKey> {
  if (stored) {
    try {
      const parsed: unknown = JSON.parse(stored);
      if (Array.isArray(parsed)) {
        return new Set(
          parsed.filter((v): v is PlaylistGroupKey =>
            (GROUP_ORDER as readonly unknown[]).includes(v),
          ),
        );
      }
    } catch {
      // durchfallen zum Default
    }
  }
  return new Set(['done']);
}

// #128: Suchfeld und Zähler über der Liste — geteilt mit der Ansicht „nach E2E-Prozess"
// (chain-groups.tsx), damit beide Sichten dieselben Zahlen unter denselben Wörtern zeigen.
export function PlaylistToolbar({
  search,
  onSearchChange,
  counts,
  hiddenCount,
}: {
  search: string;
  onSearchChange: (v: string) => void;
  counts: { open: number; waiting: number };
  hiddenCount: number;
}) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-1">
      <input
        type="search"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={t('exec.search.placeholder')}
        aria-label={t('exec.search.ariaLabel')}
        className="w-full rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-xs text-text-body"
      />
      {/* Task 10 (A4): die getrennten Zähler. Zwei Elemente statt eines Satzes, damit
          jede Zahl für sich lesbar (und prüfbar) bleibt. Der Wartend-Zähler erscheint
          nur, wenn etwas wartet: ohne Kettenmitgliedschaft kann `waiting` gar nicht
          auftreten, ein ständiges „0 wartend" wäre dort reines Rauschen.
          Whole-Branch-Review, Important 3: Der Zähler heißt „zu erledigen"
          (`exec.count.todo`), nicht mehr „offen". „Offen" trug im selben Bildausschnitt
          zwei Bedeutungen mit zwei verschiedenen Zahlen: hier alles Anfassbare (inklusive
          Fehler und Retest), eine Zeile tiefer in der Gruppenüberschrift `exec.group.open`
          nur das nie Gelaufene. Bei jedem Fehler-Eintrag standen damit zwei Zahlen unter
          demselben Wort untereinander. */}
      <div className="flex items-center gap-1 text-[11px] text-text-muted">
        <span>{t('exec.count.todo', { count: counts.open })}</span>
        {counts.waiting > 0 && (
          <>
            <span aria-hidden="true">·</span>
            <span>{t('exec.count.waiting', { count: counts.waiting })}</span>
          </>
        )}
      </div>
      {/* #78-Muster: keine stille Kappung — solange die Suche aktiv ist UND dadurch
          Einträge wegfallen, steht die Zahl direkt daneben. */}
      {hiddenCount > 0 && (
        <span className="text-[11px] text-text-muted">
          {t('exec.search.hiddenCount', { count: hiddenCount })}
        </span>
      )}
    </div>
  );
}

// #195: alles, was die Status-Ansicht für „Abgeben" braucht. Die Seite (my-tests-page.tsx) hält
// Übersicht, Auswahl und Mutation; die Playlist zeigt nur Kästchen und Leiste. Fehlt die Prop, ist
// die Rolle ohne `run.handover` — dann gibt es weder Kästchen noch Leiste.
export interface PlaylistHandover {
  overview: HandoverOverview;
  /** Markierte TESTFÄLLE (test_case_id) — weitergegeben wird der Testfall, nicht die Karte. */
  selected: ReadonlySet<string>;
  onToggle: (testCaseId: string) => void;
  onClear: () => void;
  onGive: (toUserId: string) => void;
  pending: boolean;
}

export function Playlist({
  entries,
  isLoading,
  selectedKey,
  onSelect,
  footer,
  canViewDefect,
  onOpenDefect,
  planId,
  search,
  onSearchChange,
  handover,
  fixChecks,
  selectedFixCheckId,
  onSelectFixCheck,
  onRetryFixChecks,
  fixChecksLoading = false,
}: FixCheckListProps & {
  entries: PlaylistEntry[];
  isLoading: boolean;
  // Task 3 (A6): der SCHLÜSSEL des gewählten Eintrags (playlistEntryKey), nicht seine
  // test_case_id — ein Testfall in zwei Ketten liegt zweimal in der Liste, und nur eine der
  // beiden Karten ist gewählt.
  selectedKey: string | null;
  // Task 3 (A6): meldet das PAAR zurück, nicht nur den Testfall — der Aufrufer braucht den
  // Kettenkontext (Auswahl-Zustand + Remount des ExecutionPanel).
  onSelect: (ref: PlaylistEntryRef) => void;
  // GAM-2 „gemeinsame Ziele"/Coverage-Meilensteine (Design-Spec §3.3 Playlist-Footer). Optional
  // und `null`-tolerant, damit die Spalte bei abgeschalteter Gamification (GAM-1) keine leere
  // Lücke/Border zeigt — der Aufrufer übergibt einfach nichts bzw. die Gamification-Komponenten
  // selbst rendern `null`.
  footer?: ReactNode;
  // #92: s. Kommentar an PlaylistCard oben — durchgereicht, nicht hier berechnet.
  canViewDefect: boolean;
  // #192: s. Kommentar an PlaylistCard oben — durchgereicht.
  onOpenDefect?: ((defectId: string) => void) | undefined;
  // #93: braucht die Plan-ID NUR, um den Klapp-Zustand der Gruppen pro Plan in sessionStorage zu
  // trennen (s. u.) — sonst kennt die Playlist keinen Plan. '' (noch kein Plan gewählt) ist
  // erlaubt, dann wird schlicht nicht persistiert.
  planId: string;
  search: string;
  onSearchChange: (v: string) => void;
  // #195: s. PlaylistHandover oben.
  handover?: PlaylistHandover | undefined;
}) {
  const { t } = useTranslation();

  // #195: Kästchen nur an eigenen, nicht erledigten Karten, deren Testfall der Server als
  // weitergebbar meldet (`overview.mine`). Hat der Testfall keine Ziele, bleibt das Kästchen,
  // ist aber gesperrt und nennt den Grund — sonst fragt sich der Tester, warum es hier fehlt.
  const mineByTestCase = new Map((handover?.overview.mine ?? []).map((m) => [m.test_case_id, m]));
  const handoverCheckFor = (entry: PlaylistEntry) => {
    if (!handover || !isHandoverCard(entry)) return undefined;
    const mine = mineByTestCase.get(entry.test_case_id);
    if (!mine) return undefined;
    return {
      checked: handover.selected.has(entry.test_case_id),
      disabledReason:
        mine.targets.length === 0 ? t(`handover.reason.${mine.reason ?? 'no_colleague'}`) : null,
      onToggle: () => handover.onToggle(entry.test_case_id),
    };
  };
  // Leiste: die Ziele sind die Schnittmenge aller markierten Testfälle; Name und Anzahl kommen aus
  // den Kollegen der Übersicht.
  const handoverCount = handover ? selectedEntryCount(handover.selected, entries) : 0;
  const handoverTargets = handover
    ? commonTargets(handover.selected, handover.overview.mine).flatMap((id) => {
        const colleague = handover.overview.colleagues.find((c) => c.user_id === id);
        return colleague
          ? [{ user_id: id, name: colleague.name, open: colleague.entries.length }]
          : [];
      })
    : [];

  // #93: Klapp-Zustand je Gruppe, PRO TESTPLAN in sessionStorage gemerkt — 'done' startet
  // eingeklappt (Erledigtes soll nicht den Bildschirm füllen), alle anderen Gruppen offen. Ohne
  // Plan (planId === '') wird NICHT gelesen/geschrieben: der Zustand bleibt dann reiner
  // Komponenten-State, sonst würden verschiedene Pläne sich einen Schlüssel teilen.
  const storageKey = planId ? `tf.playlist.${planId}.collapsed` : null;
  const [collapsed, setCollapsed] = useState<Set<PlaylistGroupKey>>(() =>
    readCollapsed(storageKey ? sessionStorage.getItem(storageKey) : null),
  );

  // #93 Fix (Review Task 7, Finding 1): Planwechsel ist ein PROP-Wechsel, KEIN Remount — die
  // Elternseite (my-tests-page.tsx) tauscht die Playlist ohne `key`-Wechsel aus. Der lazy
  // useState-Initializer oben liest sessionStorage nur beim ALLERERSTEN Mount; ohne diesen Effekt
  // bliebe nach einem Planwechsel A → B der Klappzustand von Plan A aktiv, und der nächste Toggle
  // würde Plan Bs bereits gespeicherten Zustand überschreiben. Läuft bei jedem storageKey-Wechsel
  // (inkl. des ersten Mounts, dort redundant zum Initializer, aber unschädlich) neu.
  useEffect(() => {
    setCollapsed(readCollapsed(storageKey ? sessionStorage.getItem(storageKey) : null));
  }, [storageKey]);

  const persist = (next: Set<PlaylistGroupKey>) => {
    if (storageKey) sessionStorage.setItem(storageKey, JSON.stringify([...next]));
  };

  const toggleGroup = (key: PlaylistGroupKey) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      persist(next);
      return next;
    });
  };

  // #93 Fix (Review Task 7, Finding 2): `entries` (bzw. das per `.find()` abgeleitete Objekt) darf
  // NICHT in die Dependency-Liste — jeder Refetch liefert neue Objekt-Referenzen für dieselben
  // Daten, sonst feuert der Effekt bei jedem Refetch erneut und reißt eine vom Nutzer bewusst
  // zugeklappte Gruppe wieder auf. Ein Ref hält deshalb die jeweils aktuellen `entries`, der Effekt
  // selbst hängt nur an der stabilen Primitive `selectedKey` (+ storageKey).
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  // #93: Auto-Aufklappen — springt die Auswahl (EXE-1, my-tests-page.tsx) in eine zugeklappte
  // Gruppe, öffnet sich diese automatisch, sonst wählte der Sprung einen unsichtbaren Eintrag.
  // Schreibt sessionStorage inline über `storageKey` (statt über `persist`) — nur so sieht der
  // Dependency-Linter, was der Effekt tatsächlich verwendet.
  useEffect(() => {
    if (!selectedKey) return;
    const selectedEntry = entriesRef.current.find((e) => playlistEntryKey(e) === selectedKey);
    if (!selectedEntry) return;
    const key = groupOf(selectedEntry);
    setCollapsed((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      if (storageKey) sessionStorage.setItem(storageKey, JSON.stringify([...next]));
      return next;
    });
  }, [selectedKey, storageKey]);

  // #194: Die gewählte Karte „Fehler prüfen" (Klick, Deeplink `?fixcheck=`, Sprung nach dem
  // Bestätigen) steht in der Gruppe „Retest" — ist sie zugeklappt, klappt sie auf, sonst landete der
  // Tester aus der Glocke vor einer geschlossenen Gruppe.
  useEffect(() => {
    if (!selectedFixCheckId) return;
    setCollapsed((prev) => {
      if (!prev.has('retest')) return prev;
      const next = new Set(prev);
      next.delete('retest');
      if (storageKey) sessionStorage.setItem(storageKey, JSON.stringify([...next]));
      return next;
    });
  }, [selectedFixCheckId, storageKey]);

  // #93: Freitextsuche über ID/Titel (dasselbe `matchesQuery` wie Katalog/Scope-Tabelle) — filtert
  // NICHT die Rohliste, sondern reicht als Sichtbarkeits-Funktion an `groupEntries` durch, damit
  // leere Gruppen samt Überschrift verschwinden statt eine leere Karten-Liste unter einem Titel
  // zu zeigen.
  const trimmed = search.trim();
  const isVisible = (e: PlaylistEntry) => matchesQuery(trimmed, e.human_id, e.title_de, e.title_en);
  // #194: Karten „Fehler prüfen" folgen derselben Suche und stehen in der Gruppe „Retest" NACH den
  // Retest-Karten (B5). Gibt es keinen Retest-Eintrag, aber Karten, erscheint die Gruppe trotzdem —
  // vorne, wie GROUP_ORDER sie stellt. Die Gruppen-Funktionen in playlist-groups.ts bleiben bei den
  // Playlist-Einträgen; die Karten werden hier dazugezählt.
  const visibleChecks = fixChecks.filter((c) => fixCheckMatches(trimmed, c));
  const entryGroups = groupEntries(entries, isVisible);
  const groups =
    visibleChecks.length > 0 && !entryGroups.some((g) => g.key === 'retest')
      ? [{ key: 'retest' as const, entries: [] as PlaylistEntry[] }, ...entryGroups]
      : entryGroups;
  const visibleCount = entries.filter(isVisible).length + visibleChecks.length;
  const hiddenCount = entries.length + fixChecks.length - visibleCount;
  // Task 10 (A4): „Zähler werden getrennt ausgewiesen: 7 offen · 5 wartend." Playlist-WEIT, nicht
  // je Gruppe — ein wartender Eintrag, dessen Vorgänger nachträglich wieder rot geworden ist, steht
  // in der gelben Gruppe „Blockiert" und würde sonst als offen gezählt (s. playlistCounts).
  // #194 (B10): jede sichtbare Karte „Fehler prüfen" ist Arbeit und zählt bei „zu erledigen" mit.
  const entryCounts = playlistCounts(entries, isVisible);
  const counts = { ...entryCounts, open: entryCounts.open + visibleChecks.length };
  const hasContent = entries.length > 0 || fixChecks.length > 0;
  // #194: Ohne Einträge entscheidet erst die Antwort der Fehlerprüfungen, ob es „nichts zu tun"
  // gibt — vorher blitzte sonst kurz der Leerzustand auf.
  const loading = isLoading || (fixChecksLoading && !hasContent);

  return (
    <div className="flex w-[360px] shrink-0 flex-col border-r border-[var(--border)]">
      <div className="flex flex-1 flex-col gap-2 overflow-y-auto p-3">
        {loading && <div className="text-sm text-text-muted">{t('common.loading')}</div>}
        {/* #194/#89: eine gescheiterte Abfrage der Fehlerprüfungen ist kein „nichts zu tun". */}
        {onRetryFixChecks && <QueryErrorNotice compact onRetry={onRetryFixChecks} />}
        {/* #194: Leerzustand nur, wenn weder Einträge noch Karten „Fehler prüfen" da sind. */}
        {!loading && !hasContent && !onRetryFixChecks && (
          <div className="text-sm text-text-muted">{t('exec.empty')}</div>
        )}
        {!isLoading && hasContent && (
          <>
            <PlaylistToolbar
              search={search}
              onSearchChange={onSearchChange}
              counts={counts}
              hiddenCount={hiddenCount}
            />
            {trimmed !== '' && visibleCount === 0 && <SearchEmptyNotice query={trimmed} />}
            {groups.map((g) => (
              <div key={g.key} className="flex flex-col gap-2">
                <button
                  type="button"
                  onClick={() => toggleGroup(g.key)}
                  className={`text-left text-[11px] font-bold uppercase tracking-wide ${GROUP_HEADING_CLASS[g.key]}`}
                >
                  {t(`exec.group.${g.key}`, {
                    count: g.entries.length + (g.key === 'retest' ? visibleChecks.length : 0),
                  })}
                </button>
                {!collapsed.has(g.key) &&
                  g.entries.map((entry) => (
                    // #93: 'Erledigt' bleibt sichtbar, aber gedämpft — kein neues Label, nur
                    // optisch zurückgenommen (PlaylistCard selbst bleibt unverändert).
                    // Task 3 (A6): React-`key` und Auswahlvergleich gehen über das Paar
                    // (Testfall, Kette). Mit `entry.test_case_id` hätten zwei Ketten-Einträge
                    // desselben Testfalls denselben key — React würde sie verwechseln — und die
                    // Auswahl würde beide Karten gleichzeitig markieren.
                    <div
                      key={playlistEntryKey(entry)}
                      className={g.key === 'done' ? 'opacity-60' : undefined}
                    >
                      <PlaylistCard
                        entry={entry}
                        selected={playlistEntryKey(entry) === selectedKey}
                        onSelect={() =>
                          onSelect({ test_case_id: entry.test_case_id, e2e_id: entry.e2e_id })
                        }
                        canViewDefect={canViewDefect}
                        onOpenDefect={onOpenDefect}
                        handoverCheck={handoverCheckFor(entry)}
                      />
                    </div>
                  ))}
                {g.key === 'retest' && !collapsed.has('retest') && (
                  <FixCheckCards
                    checks={visibleChecks}
                    selectedFixCheckId={selectedFixCheckId}
                    onSelectFixCheck={onSelectFixCheck}
                  />
                )}
              </div>
            ))}
          </>
        )}
      </div>
      {/* #195: unter der Kartenliste, über dem Gamification-Fuß, in der 360px-Spalte. */}
      {handover && handoverCount > 0 && (
        <HandoverBar
          count={handoverCount}
          targets={handoverTargets}
          pending={handover.pending}
          onGive={handover.onGive}
          onClear={handover.onClear}
        />
      )}
      {footer && <div className="border-t border-[var(--border)] p-3">{footer}</div>}
    </div>
  );
}
